<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import {
        ArcElement,
        BarController,
        BarElement,
        CategoryScale,
        Chart as ChartJS,
        DoughnutController,
        Legend,
        LinearScale,
        PieController,
        Title,
        Tooltip,
    } from 'chart.js';
    import { Chart } from 'vue-chartjs';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { setupVisibilityLoader } from '@shared/utils/visibilityLoader';
    import { forceShowCharts } from './print-charts';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import type { Nps } from '../../SurveysTypes';
    import {
        centerTextPlugin,
        csatDoughnutData,
        csatDoughnutOptions,
        horizontalBarData,
        horizontalBarOptions,
        horizontalBarRowHeight,
        npsDoughnutData,
        npsDoughnutOptions,
        npsScoreBarData,
        npsScoreBarOptions,
        pieData,
        pieLegendItems,
        pieOptions,
        resolveChartKind,
        resolveSortDefaults,
        sortOptions,
        type ChartOptionEntry,
        type NpsGroupName,
        type QuestionChartKind,
    } from './questionChartConfig';

    ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend, BarController, PieController, DoughnutController);
    applyChartDefaults();

    const props = defineProps<{
        options: ChartOptionEntry[];
        type: number;
        reportType?: number;
        responseCount: number;
        totalAverageNumericValue?: number;
        nps?: Nps;
        csatScore?: number;
        hideUnansweredOptions?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'selectOption', option: ChartOptionEntry): void;
        (e: 'selectNpsGroup', group: NpsGroupName): void;
    }>();

    const hasNumericValue = computed(() => (props.totalAverageNumericValue ?? 0) > 0);
    const naturalKind = computed<QuestionChartKind>(() => resolveChartKind(props.type, props.reportType, hasNumericValue.value));
    const isNpsOrCsat = computed(() => naturalKind.value === 'nps' || naturalKind.value === 'csat');

    const selectedKind = ref<'horizontalBar' | 'pie'>(naturalKind.value === 'pie' ? 'pie' : 'horizontalBar');
    watch(naturalKind, (kind) => {
        if (kind === 'horizontalBar' || kind === 'pie') selectedKind.value = kind;
    });
    const chartKind = computed<QuestionChartKind>(() => (isNpsOrCsat.value ? naturalKind.value : selectedKind.value));

    const sortDefaults = resolveSortDefaults(hasNumericValue.value, naturalKind.value);
    const sortProperty = ref(sortDefaults.property);
    const sortDirection = ref(sortDefaults.direction);

    function sortBy(property: typeof sortProperty.value) {
        if (property !== sortProperty.value) {
            sortProperty.value = property;
        } else {
            sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
        }
    }

    const sortedOptions = computed(() => sortOptions(props.options, sortProperty.value, sortDirection.value));
    // Zero-count options add an empty row each; with many options (e.g. a country list) they dominate the bar chart and data table.
    const visibleOptions = computed(() => (props.hideUnansweredOptions ? sortedOptions.value.filter((o) => o.ResponseCount > 0) : sortedOptions.value));

    const showDataTable = ref(false);

    // Clicking a legend item hides/shows its slice, like Chart.js' built-in legend
    const hiddenPieOrdinals = ref(new Set<number>());
    function togglePieSlice(ordinal: number) {
        const hidden = new Set(hiddenPieOrdinals.value);
        if (!hidden.delete(ordinal)) hidden.add(ordinal);
        hiddenPieOrdinals.value = hidden;
    }

    const pieLegend = computed(() => pieLegendItems(sortedOptions.value, props.responseCount, !!props.hideUnansweredOptions));

    const chartData = computed(() => {
        switch (chartKind.value) {
            case 'horizontalBar':
                return horizontalBarData(visibleOptions.value, props.responseCount);
            case 'pie':
                return pieData(sortedOptions.value, hiddenPieOrdinals.value);
            case 'nps':
                return npsDoughnutData(props.nps!);
            case 'csat':
                return csatDoughnutData(sortedOptions.value);
        }
    });

    const chartOptions = computed(() => {
        switch (chartKind.value) {
            case 'horizontalBar':
                return horizontalBarOptions(visibleOptions.value, props.responseCount, (option) => emit('selectOption', option));
            case 'pie':
                return pieOptions(sortedOptions.value, props.responseCount, (option) => emit('selectOption', option));
            case 'nps':
                return npsDoughnutOptions((group) => emit('selectNpsGroup', group));
            case 'csat':
                return csatDoughnutOptions(sortedOptions.value, props.responseCount, (option) => emit('selectOption', option));
        }
    });

    const npsScoreChartData = computed(() => npsScoreBarData(sortedOptions.value));
    const npsScoreChartOptions = computed(() => npsScoreBarOptions(sortedOptions.value, props.responseCount, (option) => emit('selectOption', option)));

    const npsCenterPlugin = [centerTextPlugin(() => (props.nps ? `NPS ${props.nps.NpsScore}` : ''))];
    const csatCenterPlugin = [centerTextPlugin(() => (props.csatScore != null ? `CSAT ${props.csatScore}%` : ''))];

    function percentage(count: number) {
        return props.responseCount > 0 ? Math.round((count / props.responseCount) * 100) : 0;
    }

    function sortIcon(property: typeof sortProperty.value) {
        if (sortProperty.value !== property) return '';
        return sortDirection.value === 'desc' ? 'glyphicon-triangle-bottom' : 'glyphicon-triangle-top';
    }

    const rootEl = ref<HTMLElement | null>(null);
    const chartsVisible = ref(false);
    const showCharts = computed(() => chartsVisible.value || forceShowCharts.value);

    // no animation when forced for print, otherwise the snapshot catches the chart mid-animation
    function printSafe<T extends object | undefined>(options: T): T {
        return forceShowCharts.value && options ? { ...options, animation: false } : options;
    }
    const renderedChartOptions = computed(() => printSafe(chartOptions.value));
    const renderedNpsScoreChartOptions = computed(() => printSafe(npsScoreChartOptions.value));

    onMounted(() => {
        // threshold 0: tall charts (many options) can never reach 10% visibility
        setupVisibilityLoader(
            rootEl.value,
            () => {
                chartsVisible.value = true;
            },
            { threshold: 0 }
        );
    });
</script>

<template>
    <div ref="rootEl">
        <div v-if="chartKind === 'nps' && nps" class="row d-flex flex-wrap align-items-center">
            <div class="flex-col col-xs-12 col-sm-5 col-md-4 col-lg-4">
                <div class="max-500 center-block">
                    <div>
                        <Chart v-if="showCharts" type="doughnut" :data="chartData" :options="renderedChartOptions" :plugins="npsCenterPlugin" />
                    </div>

                    <ul class="nps-legend d-flex flex-even list-unstyled">
                        <li class="detractor">{{ nps.DetractorPercentage }}%</li>
                        <li>{{ nps.PassivePercentage }}%</li>
                        <li class="promoter">{{ nps.PromoterPercentage }}%</li>
                    </ul>
                </div>

                <p class="text-center">
                    {{ $Resources.NpsCalculationHeading }}
                    <button
                        type="button"
                        class="popover-trigger"
                        data-toggle="popover"
                        :data-title="$Resources.NpsCalculationHeading"
                        :data-content="$Resources.NpsCalculationDescription"
                        data-placement="bottom">
                        <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                    </button>
                </p>
            </div>
            <div class="flex-col col-xs-12 col-sm-7 col-md-8 col-lg-8">
                <div class="h-400">
                    <Chart v-if="showCharts" type="bar" :data="npsScoreChartData" :options="renderedNpsScoreChartOptions" />
                </div>
            </div>
        </div>

        <div v-else-if="chartKind === 'csat'" class="h-300">
            <Chart v-if="showCharts" type="doughnut" :data="chartData" :options="renderedChartOptions" :plugins="csatCenterPlugin" />
        </div>

        <template v-else>
            <div class="p-relative" :class="chartKind === 'horizontalBar' ? 'h-auto' : 'h-300'">
                <div v-if="chartKind === 'horizontalBar'" :style="{ height: visibleOptions.length * horizontalBarRowHeight(visibleOptions) + 32 + 'px' }">
                    <Chart v-if="showCharts" type="bar" :data="chartData" :options="renderedChartOptions" />
                </div>
                <Chart v-else-if="showCharts" type="pie" :data="chartData" :options="renderedChartOptions" />
            </div>

            <ul v-if="chartKind === 'pie'" class="list-unstyled d-flex flex-wrap justify-content-center gap-lg row-gap-xs m-sm-b">
                <li
                    v-for="item in pieLegend"
                    :key="item.ordinal"
                    class="d-flex align-items-center gap-xs fw-600 cursor-pointer"
                    :class="{ 'text-line-through': hiddenPieOrdinals.has(item.ordinal) }"
                    @click="togglePieSlice(item.ordinal)">
                    <span :style="{ backgroundColor: item.color, width: '14px', height: '14px', flexShrink: 0 }"></span>
                    {{ item.text }}
                </li>
            </ul>

            <div class="hidden-print">
                <div class="d-flex gap-xs flex-wrap m-sm-b">
                    <FormRadioGroup
                        :legend="null"
                        :options="[
                            { label: $Resources.SurveyStatisticsChartTypeHorizontalBarsBtnText, value: 'horizontalBar' },
                            { label: $Resources.SurveyStatisticsChartTypePieBtnText, value: 'pie' },
                        ]"
                        :switchStyle="SwitchStyle.Button"
                        v-model="selectedKind"
                        class="btn-group btn-group-sm m-auto-r" />

                    <button v-if="!showDataTable" type="button" class="btn btn-default btn-sm" @click="showDataTable = true">
                        {{ $Resources.SurveyStatisticsShowDataTableBtnText }}
                    </button>

                    <button v-else type="button" class="btn btn-default btn-sm" @click="showDataTable = false">
                        {{ $Resources.SurveyStatisticsHideDataTableBtnText }}
                    </button>

                    <BSDropdownButton menuClass="dropdown-menu-right" btnClass="btn-sm">
                        <template #toggle>
                            <span class="glyphicon glyphicon-sort"></span>
                            <span class="sr-only">{{ $Resources.SurveyStatisticsSortDataHeading }}</span>
                        </template>
                        <template #content>
                            <li role="presentation" class="dropdown-header">{{ $Resources.SurveyStatisticsSortDataHeading }}:</li>
                            <li :class="{ active: sortProperty === 'Value' }">
                                <a href="javascript:void(0)" @click="sortBy('Value')">
                                    {{ $Resources.SurveyStatisticsSortDataByOption }}
                                    <span class="glyphicon pull-right scale-75" :class="sortIcon('Value')" aria-hidden="true"></span>
                                </a>
                            </li>
                            <li v-if="hasNumericValue" :class="{ active: sortProperty === 'NumericValue' }">
                                <a href="javascript:void(0)" @click="sortBy('NumericValue')">
                                    {{ $Resources.SurveyStatisticsSortDataByNumericValue }}
                                    <span class="glyphicon pull-right scale-75" :class="sortIcon('NumericValue')" aria-hidden="true"></span>
                                </a>
                            </li>
                            <li :class="{ active: sortProperty === 'ResponseCount' }">
                                <a href="javascript:void(0)" @click="sortBy('ResponseCount')">
                                    {{ $Resources.ResponseCountLabel }}
                                    <span class="glyphicon pull-right scale-75" :class="sortIcon('ResponseCount')" aria-hidden="true"></span>
                                </a>
                            </li>
                            <li :class="{ active: sortProperty === 'Ordinal' }">
                                <a href="javascript:void(0)" @click="sortBy('Ordinal')">
                                    {{ $Resources.SurveyStatisticsSortDataByOrdinal }}
                                    <span class="glyphicon pull-right scale-75" :class="sortIcon('Ordinal')" aria-hidden="true"></span>
                                </a>
                            </li>
                        </template>
                    </BSDropdownButton>
                </div>
            </div>

            <div v-if="showDataTable" class="custom-scrollbar mx-h-300 m-lg-b">
                <div class="table-responsive">
                    <table class="table table-hover align-middle m-0-b">
                        <thead>
                            <tr>
                                <th class="cursor-pointer" @click="sortBy('Value')">
                                    {{ $Resources.SurveyStatisticsSortDataByOption }}
                                    <span class="glyphicon m-sm-l scale-75" :class="sortIcon('Value')" aria-hidden="true"></span>
                                </th>
                                <th class="cursor-pointer text-right text-nowrap" @click="sortBy('ResponseCount')">
                                    {{ $Resources.ResponseCountLabel }}
                                    <span class="glyphicon m-sm-l scale-75" :class="sortIcon('ResponseCount')" aria-hidden="true"></span>
                                </th>
                                <th class="text-right text-nowrap autofit">%</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="option in visibleOptions" :key="option.Ordinal">
                                <td>
                                    <strong>{{ option.Value }}</strong>
                                </td>
                                <td
                                    class="text-right text-nowrap"
                                    :class="{ 'c-danger strong': option.UseMaxAnswers && option.ResponseCount >= (option.MaxAnswers ?? 0) }">
                                    {{ option.ResponseCount }}
                                    <span v-if="option.UseMaxAnswers">{{ $Resources.SurveyStatisticsMaxAnswersLabel }} {{ option.MaxAnswers }}</span>
                                </td>
                                <td class="text-right text-nowrap">{{ percentage(option.ResponseCount) }}%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

